import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader } from '@/components/loader';
import { useToast } from '@/components/ui/use-toast';
import { ChevronLeft, Plus, EyeOff } from 'lucide-react';
import StaffAPI from '@/api/staff.api';
import AddSubscriptionDialog from '../components/add-subscription-dialog';
import UpdateSubscriptionDialog from '../components/update-subscription-dialog';

const fullName = u => [u.firstName, u.lastName].filter(Boolean).join(' ').trim();

export default function StaffUserDetail() {
  const { id: userId } = useParams();
  const [user, setUser] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editingSub, setEditingSub] = useState(null);
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, subsRes] = await Promise.all([
        StaffAPI.list(),
        StaffAPI.listSubscriptions({ userId }),
      ]);

      if (listRes.error || subsRes.error) {
        toast({
          title: 'Failed to load staff user',
          description:
            listRes.data?.message ||
            subsRes.data?.message ||
            listRes.error?.message ||
            subsRes.error?.message ||
            'Unexpected error',
          variant: 'destructive',
        });
        return;
      }

      const found = (listRes.data?.users ?? []).find(u => u.id?.toString() === userId.toString());
      setUser(found ?? null);
      setSubscriptions(subsRes.data?.subscriptions ?? []);
    } finally {
      setLoading(false);
    }
  }, [userId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <Loader />
        </CardContent>
      </Card>
    );
  }

  if (!user) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-sm text-muted-foreground">
            User not found in the staff organization.
          </div>
          <Link
            to="/dashboard/staff"
            className="inline-flex items-center text-sm mt-2 hover:underline">
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to C8 user subscriptions
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Link
        to="/dashboard/staff"
        className="inline-flex items-center text-sm mb-4 text-muted-foreground hover:underline">
        <ChevronLeft className="h-4 w-4 mr-1" />
        Back to C8 user subscriptions
      </Link>

      <Card className="mb-4">
        <CardHeader>
          <div>
            <CardTitle className="font-mono text-base">{user.email}</CardTitle>
            <CardDescription>{fullName(user) || 'No name on file'}</CardDescription>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Subscriptions</CardTitle>
            <CardDescription>
              {subscriptions.length} subscription(s) across organizations (active and hidden).
            </CardDescription>
          </div>
          <Button onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add subscription
          </Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Organization</TableHead>
                <TableHead>Permissions</TableHead>
                <TableHead>Visibility</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subscriptions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">
                    No subscriptions.
                  </TableCell>
                </TableRow>
              ) : (
                subscriptions.map(sub => (
                  <TableRow key={sub.id}>
                    <TableCell>
                      <div>{sub.organizationName || '—'}</div>
                      <div className="text-xs text-muted-foreground font-mono">
                        {sub.organizationId}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{sub.permissions}</Badge>
                    </TableCell>
                    <TableCell>
                      {sub.isHidden ? (
                        <span className="inline-flex items-center text-xs text-muted-foreground">
                          <EyeOff className="h-3.5 w-3.5 mr-1" />
                          Hidden
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">Visible</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="outline" size="sm" onClick={() => setEditingSub(sub)}>
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <AddSubscriptionDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        userId={userId}
        userEmail={user.email}
        existingOrgIds={subscriptions.map(s => s.organizationId)}
        onSuccess={() => {
          setAddOpen(false);
          load();
        }}
      />

      <UpdateSubscriptionDialog
        userId={userId}
        subscription={editingSub}
        onClose={() => setEditingSub(null)}
        onSuccess={() => {
          setEditingSub(null);
          load();
        }}
      />
    </>
  );
}
